import React from 'react';
import {
  Autocomplete, Box, Checkbox, Chip, MenuItem, TextField, Typography,
} from '@mui/material';
import { colors } from '../theme/designSystem';
import { getDefaultTextFieldSx } from '../theme/textFieldStyles';
import {
  FILE_VISIBILITIES, FileAccess, FileAccessOption, FileVisibility,
} from '../services/patientFilesService';

/**
 * "Quem pode ver" de arquivo do paciente (RN-538d). Mesmo desenho dos controles que o sistema já
 * usa: seleção de modo como nas Anotações e seleção múltipla com chips como na Biblioteca. Usado no
 * envio, na edição de um arquivo e — com `profilesAndUsersOnly` — na liberação de todos os arquivos.
 */
interface Props {
  value: FileAccess;
  onChange: (value: FileAccess) => void;
  profiles: FileAccessOption[];
  users: FileAccessOption[];
  /** Só perfis e usuários, sem o seletor de modo (liberar todos os arquivos do paciente). */
  profilesAndUsersOnly?: boolean;
  disabled?: boolean;
}

/**
 * Seleção múltipla com busca: a lista de usuários passa de 30 nomes, rolar não serve. Um campo por
 * linha, chips que quebram dentro do campo e opção com caixa de marcar — a lista fica aberta enquanto
 * se marcam vários.
 */
const MultiSelect: React.FC<{
  label: string;
  placeholder: string;
  selected: string[];
  options: FileAccessOption[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}> = ({ label, placeholder, selected, options, onChange, disabled }) => {
  const value = options.filter((o) => selected.includes(o.id));
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      value={value}
      disabled={disabled}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, items) => onChange(items.map((o) => o.id))}
      noOptionsText="Nada encontrado"
      renderOption={(props, option, { selected: checked }) => (
        <li {...props} key={option.id}>
          <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0.5 }} />
          {option.name}
        </li>
      )}
      renderTags={(items, getTagProps) =>
        items.map((o, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} {...tagProps} label={o.name} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          InputLabelProps={{ shrink: true }}
          sx={getDefaultTextFieldSx()}
        />
      )}
    />
  );
};

const PatientFileAccessFields: React.FC<Props> = ({
  value, onChange, profiles, users, profilesAndUsersOnly = false, disabled = false,
}) => {
  const current = FILE_VISIBILITIES.find((v) => v.value === value.visibility) ?? FILE_VISIBILITIES[0];
  const showTargets = profilesAndUsersOnly || value.visibility === 'custom';
  const missingTargets = value.visibility === 'custom' && !profilesAndUsersOnly
    && value.profileIds.length === 0 && value.userIds.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!profilesAndUsersOnly && (
        <TextField
          select
          fullWidth
          size="small"
          label="Quem pode ver"
          value={value.visibility}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, visibility: e.target.value as FileVisibility })}
          InputLabelProps={{ shrink: true }}
          helperText={current.help}
          sx={getDefaultTextFieldSx()}
        >
          {FILE_VISIBILITIES.map((v) => (
            <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
          ))}
        </TextField>
      )}

      {showTargets && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <MultiSelect
            label="Perfis"
            placeholder="Buscar perfil"
            selected={value.profileIds}
            options={profiles}
            disabled={disabled}
            onChange={(profileIds) => onChange({ ...value, profileIds })}
          />
          <MultiSelect
            label="Usuários"
            placeholder="Buscar pelo nome"
            selected={value.userIds}
            options={users}
            disabled={disabled}
            onChange={(userIds) => onChange({ ...value, userIds })}
          />
        </Box>
      )}

      {showTargets && (
        <Typography
          variant="caption"
          sx={{ color: missingTargets ? colors.error : colors.textSecondary, mt: -1 }}
        >
          {missingTargets
            ? 'Escolha ao menos um perfil ou usuário.'
            : 'As duas listas se somam: abre quem estiver em um dos perfis OU for um dos usuários. O perfil vale para quem estiver nele, inclusive quem entrar depois; só alcança quem tem acesso a este paciente.'}
        </Typography>
      )}
    </Box>
  );
};

export default PatientFileAccessFields;
